import React, { useEffect, useState } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";

import {
  DndContext,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToHorizontalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  SortableContext,
  horizontalListSortingStrategy,
} from "@dnd-kit/sortable";
import ImageLoading from "../components/ImageLoading";
import NoDataFound from "../components/NoDataFound";
import { FiMove } from "react-icons/fi";
import { BiSolidSortAlt } from "react-icons/bi";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

function TableComponent({
  imageLoading,
  list,
  columns,
  visibleColumns,
  setLeafColumns,

  columnOrder,
  setColumnOrder,
  pinnedColumns,
  handleClick,
  setColumnSettings,
}) {
  const table = useReactTable({
    data: list,
    columns,
    getCoreRowModel: getCoreRowModel(),
    state: {
      columnVisibility: visibleColumns,
      columnOrder: columnOrder,
    },
    initialState: {
      columnPinning: {
        left: pinnedColumns,
        right: [],
      },
      //...
    },
    onColumnOrderChange: setColumnOrder,
  });

  useEffect(() => {
    setLeafColumns(table.getAllLeafColumns());
  }, []);
  //DND-Table-code
  function handleDragEnd(event) {
    const { active, over } = event;
    if (active && over && active.id !== over.id) {
      setColumnOrder((columnOrder) => {
        const oldIndex = columnOrder.indexOf(active.id);
        const newIndex = columnOrder.indexOf(over.id);
        return arrayMove(columnOrder, oldIndex, newIndex);
      });
    }
  }

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );
  return (
    <>
      <DndContext
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={handleDragEnd}
        sensors={sensors}
      >
        {imageLoading && (
          <div className="w-full h-[calc(100vh-248px)] flex items-center justify-center">
            <ImageLoading />
          </div>
        )}
        {list.length < 1 && !imageLoading ? (
          <div className="w-full h-[calc(100vh-248px)] flex items-center justify-center">
            <NoDataFound />
          </div>
        ) : (
          <>
            {!imageLoading && (
              <div
                onScroll={() => {
                  setColumnSettings(false);
                }}
                className=" max-h-[calc(100vh-230px)]   relative overflow-y-scroll border-x border-t border-[#dbdee2] rounded-lg  rounded-b-none  "
              >
                <table
                  style={{
                    width: table.getTotalSize(),
                    zIndex: 0,
                  }}
                  className=" min-w-[100%]  "
                >
                  <thead className=" sticky top-0 z-[30]">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr
                        className=" text-[13px] font-semibold text-[#393939]  border-b border-[#dbdee2] bg-[#f3f5f6] "
                        key={headerGroup.id}
                      >
                        <SortableContext
                          items={columnOrder}
                          strategy={horizontalListSortingStrategy}
                        >
                          {headerGroup.headers.map((header) => (
                            <DraggableTableHeader
                              key={header.id}
                              header={header}
                            />
                            // <th className={` text-left py-2 pl-3 `} key={header.id}>
                            //   {header.isPlaceholder
                            //     ? null
                            //     : flexRender(
                            //         header.column.columnDef.header,
                            //         header.getContext()
                            //       )}
                            // </th>
                          ))}
                        </SortableContext>
                      </tr>
                    ))}
                  </thead>
                  <div className="w-full max-h-[calc(100vh-300px)] overflow-y-scroll"></div>
                  <tbody className=" w-full max-h-[calc(100vh-300px)]">
                    {table?.getRowModel()?.rows.map((row) => {
                      return (
                        <tr
                          onClick={() => {
                            const singleRow = row.original;
                            handleClick(singleRow);
                            // onClick(
                            //   singleRow.id,
                            //   singleRow.name,
                            //   singleRow.credit_type
                            // );
                          }}
                          className=" text-xs text-[#393939] border-b border-[#dbdee2] cursor-pointer   "
                          key={row.id}
                        >
                          {row.getVisibleCells().map((cell) => {
                            const { column } = cell;
                            return (
                              <td
                                className={` text-left py-2 pl-3  ] `}
                                style={{
                                  ...getCommonPinningStyles(column, false),
                                }}
                                key={cell.id}
                              >
                                {flexRender(
                                  cell.column.columnDef.cell,
                                  cell.getContext()
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </DndContext>
    </>
  );
}

export default TableComponent;

const getCommonPinningStyles = (column, isHeader) => {
  const isPinned = column.getIsPinned();
  const isLastLeftPinnedColumn =
    isPinned === "left" && column.getIsLastColumn("left");
  const isFirstRightPinnedColumn =
    isPinned === "right" && column.getIsFirstColumn("right");

  return {
    boxShadow: isLastLeftPinnedColumn
      ? "-1px 0 1px -1px #00394d inset"
      : isFirstRightPinnedColumn
      ? "4px 0 4px -4px #00394D inset"
      : undefined,
    left: isPinned === "left" ? `${column.getStart("left")}px` : undefined,
    right: isPinned === "right" ? `${column.getAfter("right")}px` : undefined,
    opacity: isPinned ? 0.9 : 1,
    position: isPinned ? "sticky" : "relative",
    backgroundColor: isPinned ? (isHeader ? "#f3f5f6" : "white") : undefined,

    width: column.getSize(),

    zIndex: isPinned ? 20 : 0,
    className: "hoverable-cell",
  };
};

//Table Header Cell JSX
const DraggableTableHeader = ({ header }) => {
  const { attributes, isDragging, listeners, setNodeRef, transform } =
    useSortable({
      id: header.column.id,
    });
  const { column } = header;

  const style = {
    opacity: isDragging ? 0.8 : 1,

    transform: CSS.Translate.toString(transform),
    transition: "width transform 0.2s ease-in-out",

    zIndex: isDragging ? 1 : 0,
  };

  return (
    <th
      className={` text-left py-2 pl-3 group `}
      colSpan={header.colSpan}
      ref={setNodeRef}
      style={{ ...style, ...getCommonPinningStyles(column, true) }}
    >
      <span className=" flex items-center">
        {header.isPlaceholder
          ? null
          : flexRender(header.column.columnDef.header, header.getContext())}
        {column.getCanSort() && (
          <button className="    ml-2">
            <BiSolidSortAlt color="#606668" size={13} />
          </button>
        )}

        {!column.getIsPinned() && (
          <button
            className=" invisible group-hover:visible  ml-2"
            {...attributes}
            {...listeners}
          >
            <FiMove />
          </button>
        )}
      </span>
    </th>
  );
};
